/**
 * The document format bar's actions (stage 3). Each one edits the markdown of
 * the block being edited, around the selection, and never adds words of its
 * own: a heading or list mark on the lines, emphasis marks around the words,
 * a link's brackets with the address left for the person to type.
 */

export type FormatAction = "h2" | "h3" | "bold" | "italic" | "list" | "link";

export type TextEdit = { text: string; selectionStart: number; selectionEnd: number };

const HEADING = /^ {0,3}#{1,6}[ \t]+/;
const BULLET = /^( {0,3})[-*+][ \t]+/;

/** The lines the selection touches: [start, end) of the text. */
function lineSpan(text: string, from: number, to: number): { start: number; end: number } {
  const start = text.lastIndexOf("\n", from - 1) + 1;
  // A selection that ends right after a line break does not take the next line.
  const last = to > from && text[to - 1] === "\n" ? to - 1 : to;
  const nl = text.indexOf("\n", last);
  return { start, end: nl < 0 ? text.length : nl };
}

function mapLines(
  text: string,
  from: number,
  to: number,
  change: (lines: string[]) => string[],
): TextEdit {
  const span = lineSpan(text, from, to);
  const before = text.slice(span.start, span.end);
  const lines = before.split("\n");
  const after = change(lines).join("\n");
  const next = text.slice(0, span.start) + after + text.slice(span.end);
  if (from === to) {
    // A caret stays with its words; the first line's mark moved them.
    const delta = change([lines[0]])[0].length - lines[0].length;
    const caret = Math.max(span.start, from + delta);
    return { text: next, selectionStart: caret, selectionEnd: caret };
  }
  return { text: next, selectionStart: span.start, selectionEnd: span.start + after.length };
}

function heading(level: 2 | 3, lines: string[]): string[] {
  const mark = `${"#".repeat(level)} `;
  const all = lines.every((line) => !line.trim() || line.startsWith(mark));
  return lines.map((line) => {
    if (!line.trim()) return line;
    const bare = line.replace(HEADING, "");
    return all ? bare : mark + bare;
  });
}

function list(lines: string[]): string[] {
  const all = lines.every((line) => !line.trim() || BULLET.test(line));
  return lines.map((line) => {
    if (!line.trim()) return line;
    if (all) return line.replace(BULLET, "$1");
    return BULLET.test(line) ? line : `- ${line.replace(HEADING, "")}`;
  });
}

function wrap(text: string, from: number, to: number, mark: string): TextEdit {
  const n = mark.length;
  // Already marked just outside the selection: take the marks away.
  if (text.slice(from - n, from) === mark && text.slice(to, to + n) === mark) {
    return {
      text: text.slice(0, from - n) + text.slice(from, to) + text.slice(to + n),
      selectionStart: from - n,
      selectionEnd: to - n,
    };
  }
  const picked = text.slice(from, to);
  // Marked just inside it: the same.
  if (picked.length > 2 * n && picked.startsWith(mark) && picked.endsWith(mark)) {
    const inner = picked.slice(n, -n);
    return {
      text: text.slice(0, from) + inner + text.slice(to),
      selectionStart: from,
      selectionEnd: from + inner.length,
    };
  }
  // Spaces at the edges stay outside the marks, or the markdown would not read as emphasis.
  const lead = picked.length - picked.trimStart().length;
  const trail = picked.length - picked.trimEnd().length;
  const start = from + lead;
  const end = Math.max(start, to - trail);
  const words = text.slice(start, end);
  return {
    text: text.slice(0, start) + mark + words + mark + text.slice(end),
    selectionStart: start + n,
    selectionEnd: start + n + words.length,
  };
}

function link(text: string, from: number, to: number): TextEdit {
  const words = text.slice(from, to);
  const inserted = `[${words}]()`;
  // With words picked, the caret waits where the address goes; with none, where the words go.
  const caret = words ? from + inserted.length - 1 : from + 1;
  return {
    text: text.slice(0, from) + inserted + text.slice(to),
    selectionStart: caret,
    selectionEnd: caret,
  };
}

export function applyFormat(
  action: FormatAction,
  text: string,
  selectionStart: number,
  selectionEnd: number,
): TextEdit {
  const from = Math.min(selectionStart, selectionEnd);
  const to = Math.max(selectionStart, selectionEnd);
  switch (action) {
    case "h2":
      return mapLines(text, from, to, (lines) => heading(2, lines));
    case "h3":
      return mapLines(text, from, to, (lines) => heading(3, lines));
    case "list":
      return mapLines(text, from, to, list);
    case "bold":
      return wrap(text, from, to, "**");
    case "italic":
      return wrap(text, from, to, "_");
    case "link":
      return link(text, from, to);
  }
}

/** A passage as a markdown quote, line by line (Rewrite puts it in the thread's composer). */
export function quotePassage(passage: string): string {
  return passage
    .trim()
    .split("\n")
    .map((line) => (line.trim() ? `> ${line}` : ">"))
    .join("\n");
}
